import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Clock, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['All', 'GED', 'SAT', 'College'];

export default function Courses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const params = {};
        if (selectedCategory && selectedCategory !== 'All') {
          params.category = selectedCategory;
        }
        const { data } = await axios.get(`${API_URL}/api/courses`, { params });
        setCourses(data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [selectedCategory]);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    if (value === 'All') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryBadgeClass = (category) => {
    switch (category) {
      case 'GED': return 'badge-ged';
      case 'SAT': return 'badge-sat';
      case 'College': return 'badge-college';
      default: return 'badge-ged';
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="courses-page">
      {/* Header */}
      <div className="bg-[#F9FAFB] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">
            OUR COURSES
          </span>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-[#0A0A0A] mb-4">
            {selectedCategory === 'All' ? 'All Courses' : `${selectedCategory} Preparation`}
          </h1>
          <p className="text-[#4B5563] text-lg max-w-2xl">
            Comprehensive courses designed to help you succeed in your academic journey. 
            Learn at your own pace with expert instruction.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-[#E5E7EB] py-6 px-6 sticky top-[73px] bg-white z-40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
            <Input
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-sm border-[#E5E7EB]"
              data-testid="course-search"
            />
          </div>
          <div className="flex gap-2">
            {CATEGORIES.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                className={`rounded-sm ${selectedCategory === cat ? 'bg-[#002FA7] hover:bg-[#001B63]' : 'border-[#E5E7EB]'}`}
                onClick={() => handleCategoryChange(cat)}
                data-testid={`filter-${cat.toLowerCase()}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#4B5563] text-lg">No courses found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course, index) => (
                <div key={course.id} className="card tutor-card" data-testid={`course-card-${index}`}>
                  {course.image_url && (
                    <img 
                      src={course.image_url} 
                      alt={course.title}
                      className="w-full h-48 object-cover mb-4"
                    />
                  )}
                  <span className={`${getCategoryBadgeClass(course.category)} inline-block mb-3`}>
                    {course.category}
                  </span>
                  <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">{course.title}</h3>
                  <p className="text-[#4B5563] text-sm mb-4 line-clamp-3">{course.description}</p>
                  
                  <div className="flex items-center gap-4 mb-4 text-sm text-[#4B5563]">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{course.duration_hours} hours</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs bg-[#F3F4F6] px-2 py-1">{course.subject}</span>
                    </div>
                  </div>

                  {course.tutor_name && (
                    <p className="text-sm text-[#4B5563] mb-4">
                      Instructor: <span className="font-medium text-[#0A0A0A]">{course.tutor_name}</span>
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-[#E5E7EB]">
                    <div>
                      <span className="text-2xl font-bold text-[#002FA7]">${course.price}</span>
                    </div>
                    <Link to={`/courses/${course.id}`}>
                      <Button className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm" data-testid={`view-course-${index}`}>
                        View Course
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
